import { create } from 'zustand';
import { apiFetch } from '../lib/apiFetch';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export type Notification = {
  id: number;
  recipient_id: string;
  task_id: string;
  type: string;
  title: string;
  message: string; // JSON string with workerName, clientName, taskTitle, reason
  is_read: boolean;
  sent_via: string;
  created_at: string;
  read_at?: string;
  task_title?: string;
  task_description?: string;
  task_status?: string;
};

// Play a soft notification beep via Web Audio API (no external file needed)
export function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (_) {
    // Silently fail if AudioContext is not available
  }
}

type NotificationStore = {
  notifications: Notification[];
  notificationHistory: Notification[];
  unreadCount: number;
  newCount: number; // notifications arrived since last "seen" — use to trigger sound
  isLoading: boolean;
  error: string | null;
  lastCheckTime: number;

  fetchUnreadNotifications: (userId: string) => Promise<void>;
  fetchNotificationHistory: (userId: string) => Promise<void>;
  clearNew: () => void;
  markAsRead: (notificationId: number) => Promise<void>;
  markHistoryAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: (userId: string) => Promise<void>;
  markAllHistoryAsRead: () => Promise<void>;
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  notificationHistory: [],
  unreadCount: 0,
  newCount: 0,
  isLoading: false,
  error: null,
  lastCheckTime: 0,

  fetchUnreadNotifications: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiFetch(`${API_BASE}/notifications/unread?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch notifications');

      const data: Notification[] = await response.json();
      const currentTime = Date.now();
      const prevIds = new Set(get().notifications.map(n => n.id));
      const arrived = data.filter(n => !prevIds.has(n.id)).length;

      set({
        notifications: data,
        unreadCount: data.length,
        // Only bump newCount if we already did at least one fetch (lastCheckTime > 0)
        newCount: get().lastCheckTime > 0 ? arrived : 0,
        isLoading: false,
        lastCheckTime: currentTime,
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      set({ error: 'Failed to fetch notifications', isLoading: false });
    }
  },

  fetchNotificationHistory: async (userId: string) => {
    try {
      let response = await apiFetch(`${API_BASE}/notifications/history`);
      if (response.status === 404) {
        response = await apiFetch(`${API_BASE}/notifications/unread?userId=${encodeURIComponent(userId)}`);
        if (!response.ok) throw new Error('Failed to fetch unread notifications');

        const unread: Notification[] = await response.json();
        const historyById = new Map(get().notificationHistory.map(notification => [notification.id, notification]));
        for (const notification of unread) historyById.set(notification.id, notification);
        const data = Array.from(historyById.values())
          .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
          .slice(0, 100);
        const previousIds = new Set(get().notificationHistory.map(notification => notification.id));
        const arrived = unread.filter(notification => !previousIds.has(notification.id)).length;
        const hasCheckedBefore = get().lastCheckTime > 0;

        set({
          notificationHistory: data,
          unreadCount: unread.length,
          newCount: hasCheckedBefore ? arrived : 0,
          isLoading: false,
          error: null,
          lastCheckTime: Date.now(),
        });
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch notification history');

      const data: Notification[] = await response.json();
      const previousIds = new Set(get().notificationHistory.map(notification => notification.id));
      const arrived = data.filter(notification => !notification.is_read && !previousIds.has(notification.id)).length;
      const hasCheckedBefore = get().lastCheckTime > 0;

      set({
        notificationHistory: data,
        unreadCount: data.filter(notification => !notification.is_read).length,
        newCount: hasCheckedBefore ? arrived : 0,
        isLoading: false,
        error: null,
        lastCheckTime: Date.now(),
      });
    } catch (error) {
      console.error('Error fetching notification history:', error);
      set({ error: 'Failed to fetch notifications', isLoading: false });
    }
  },

  clearNew: () => set({ newCount: 0 }),

  markAsRead: async (notificationId: number) => {
    try {
      const response = await apiFetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to mark notification as read');

      set(state => ({
        notifications: state.notifications.filter(n => n.id !== notificationId),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markHistoryAsRead: async (notificationId: number) => {
    const notification = get().notificationHistory.find(item => item.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      const response = await apiFetch(`${API_BASE}/notifications/${notificationId}/read`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to mark notification as read');

      const readAt = new Date().toISOString();
      set(state => ({
        notificationHistory: state.notificationHistory.map(item =>
          item.id === notificationId ? { ...item, is_read: true, read_at: readAt } : item
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      const response = await apiFetch(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      if (!response.ok) throw new Error('Failed to mark all notifications as read');

      set({ notifications: [], unreadCount: 0, newCount: 0 });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  },

  markAllHistoryAsRead: async () => {
    try {
      const response = await apiFetch(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error('Failed to mark all notifications as read');

      const readAt = new Date().toISOString();
      set(state => ({
        notificationHistory: state.notificationHistory.map(item => ({ ...item, is_read: true, read_at: item.read_at || readAt })),
        unreadCount: 0,
        newCount: 0,
      }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }
}));
